import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PaymentService } from '../../services/payments/payment/payment.service';
import { DirectApplyCheckoutService } from '../../services/payments/direct-apply-checkout/direct-apply-checkout.service';
import { TransactionDetailResponse } from '../../models/payment/transactionDetailResponse.model';

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [CommonModule, ButtonModule, ToastModule],
  providers: [MessageService],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss'
})
export class PaymentComponent implements OnChanges {
  /** Merchant transaction number returned by the gateway callback. */
  @Input() txnId: string | null = null;
  /** true = payment succeeded, false = failed, null = no payment result yet. */
  @Input() isSuccess: boolean | null = null;
  /** Failure reason supplied by the gateway when the payment failed. */
  @Input() failureReason: string | null = null;

  /** Emitted when the user proceeds to the confirmation step after a successful payment. */
  @Output() onNext = new EventEmitter<void>();

  transaction: TransactionDetailResponse | null = null;
  loadingTransaction = false;
  retrying = false;

  constructor(
    private paymentService: PaymentService,
    private checkoutService: DirectApplyCheckoutService,
    private messageService: MessageService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['txnId'] || changes['isSuccess']) && this.isSuccess && this.txnId) {
      this.loadTransaction(this.txnId);
    }
  }

  private loadTransaction(txnId: string): void {
    this.loadingTransaction = true;
    this.paymentService.getTransaction(txnId).subscribe({
      next: (data: TransactionDetailResponse) => {
        this.transaction = data;
        this.loadingTransaction = false;
      },
      error: (err) => {
        console.error('Failed to load transaction details', err);
        this.loadingTransaction = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load transaction details. Please try again later.'
        });
      }
    });
  }

  continueToConfirmation(): void {
    this.onNext.emit();
  }

  /** Re-initiates the Direct Apply payment and sends the user back to the gateway. */
  retryPayment(): void {
    if (this.retrying) return;
    this.retrying = true;

    const sourceUrl = window.location.origin + '/portal/direct-apply/payment-review';
    this.checkoutService.initiatePayment(sourceUrl).subscribe({
      next: (response) => {
        if (response.redirectUri && response.redirectUri.trim() !== '') {
          window.location.href = response.redirectUri;
        } else {
          this.retrying = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Could not start the payment. Please try again.'
          });
        }
      },
      error: (err) => {
        console.error('Failed to retry payment', err);
        this.retrying = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to start the payment. Please try again.'
        });
      }
    });
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  }

  /** Generates a PDF of the on-screen receipt (same approach as the pay-now screen). */
  downloadReceipt(): void {
    const receiptArea = document.getElementById('receiptPdfArea');
    if (!receiptArea) {
      this.messageService.add({
        severity: 'error',
        summary: 'Receipt Download Error',
        detail: 'Failed to download receipt.'
      });
      return;
    }

    (document as any).fonts.ready.then(() => {
      html2canvas(receiptArea, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        removeContainer: true,
        onclone: (clonedDoc: any) => {
          const clonedArea = clonedDoc.getElementById('receiptPdfArea');
          if (clonedArea) {
            clonedArea.style.width = '768px';
            clonedArea.style.maxWidth = '768px';
            clonedArea.style.margin = '0 auto';
            clonedArea.style.fontWeight = '500';
            clonedArea.style.color = '#101010';
            clonedArea.querySelectorAll('*').forEach((node: any) => {
              node.style.opacity = '1';
              node.style.filter = 'none';
              node.style.textShadow = 'none';
              node.style.fontWeight = window.getComputedStyle(node).fontWeight;
            });
          }
        }
      } as any).then(canvas => {
        const imgData = canvas.toDataURL('image/png', 1.0);
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = 210;
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'NONE');
        pdf.save(`Gradpilots Receipt-${this.transaction?.transactionId || this.txnId}.pdf`);

        this.messageService.add({
          severity: 'success',
          summary: 'Receipt Downloaded',
          detail: 'Your receipt has been downloaded successfully.'
        });
      }).catch((err: any) => {
        console.error('Failed to generate receipt PDF', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Receipt Download Error',
          detail: 'Failed to download receipt.'
        });
      });
    });
  }
}
